import React, { useEffect, useRef, useState } from 'react';
import './recipePdfViewer.css';

/**
 * RecipePdfViewer — shows the generated Recipe Book PDF inside the application.
 *
 * WHY THIS EXISTS
 * The book is generated client-side with jsPDF, and the generator used to end with
 * `pdf.save()` — which is the download. There was no way to look at the book without
 * a file landing in Downloads. exportAllRecipesPdf({ output: 'blob' }) now hands the
 * document back instead, and this renders it.
 *
 * HOW IT RENDERS
 * The blob becomes an object URL fed to an <iframe>. The browser's own PDF plugin does
 * the rendering, so there is no pdf.js dependency to ship, no worker to configure and
 * no second renderer that could disagree with the file the user actually downloads —
 * what is on screen IS the file, byte for byte.
 *
 * Some browsers (iOS Safari, most Android browsers, Firefox with the plugin disabled)
 * will not render a PDF in an iframe. They fail SILENTLY — a blank white box, no error
 * event — so this does not wait to be told: it detects those up front and shows an
 * honest fallback with "Open in new tab" and "Download" instead of a blank rectangle.
 *
 * LIFECYCLE
 * Object URLs pin the blob in memory until revoked. This revokes on close and on
 * unmount, so opening the viewer ten times does not hold ten copies of the book.
 */

/** Can this browser be trusted to render a PDF inside an iframe? */
function canRenderInlinePdf() {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  // iOS renders the first page only and ignores scrolling inside an iframe; Android's
  // stock browsers hand the PDF off to an external app or show nothing at all.
  if (/iPad|iPhone|iPod/.test(ua) || /Android/i.test(ua)) return false;

  // The modern signal. Chrome, Edge and Safari on desktop report true; Firefox reports
  // true when pdf.js is enabled, which is the default.
  if (typeof navigator.pdfViewerEnabled === 'boolean') return navigator.pdfViewerEnabled;

  // Older browsers: fall back to the mime-type registry.
  const types = navigator.mimeTypes;
  if (types && typeof types.namedItem === 'function') {
    return Boolean(types.namedItem('application/pdf'));
  }
  return true;
}

/**
 * @param {Blob}   blob      the generated PDF
 * @param {string} fileName  the name it downloads as
 * @param {number} pageCount pages, for the header
 * @param {string} title     what the book is (business name)
 * @param {Function} onClose
 */
export default function RecipePdfViewer({ blob, fileName, pageCount, title, subtitle, onClose, onEditQuantities }) {
  const [url, setUrl] = useState(null);
  const [inlineSupported] = useState(canRenderInlinePdf);
  const [downloaded, setDownloaded] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    if (!blob) return undefined;
    const objectUrl = URL.createObjectURL(blob);
    urlRef.current = objectUrl;
    setUrl(objectUrl);

    return () => {
      // Revoked on unmount, never left dangling: an un-revoked object URL keeps the
      // whole PDF in memory for the life of the tab.
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [blob]);

  // Escape closes, like every other modal in the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * Download — the ONLY place a file is written, and only when the user asks.
   *
   * Deliberately not `pdf.save()` again: re-running the generator would rebuild the
   * document from scratch and could produce a file that differs from the one being
   * previewed. This saves the exact bytes on screen.
   */
  const handleDownload = () => {
    if (!urlRef.current) return;
    const a = document.createElement('a');
    a.href = urlRef.current;
    a.download = fileName || 'Recipe_Book.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDownloaded(true);
  };

  const handleOpenInTab = () => {
    if (urlRef.current) window.open(urlRef.current, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rpv-overlay" role="dialog" aria-modal="true" aria-label="Recipe Book preview">
      <div className="rpv-modal">
        <header className="rpv-head">
          <div className="rpv-head-text">
            <h2>{title || 'Recipe Book'}</h2>
            <p>
              {subtitle ? `${subtitle} · ` : ''}
              {pageCount ? `${pageCount} page${pageCount === 1 ? '' : 's'}` : 'PDF preview'}
              {downloaded && <span className="rpv-saved"> · saved</span>}
            </p>
          </div>

          <div className="rpv-head-actions">
            {/* This document is a snapshot. Corrections happen in the editable book, and
                without a way through, the only route to it is knowing the nav entry
                exists. Rendered only when the caller can actually offer it (owner +
                feature flag), so nobody is shown a door they cannot open. */}
            {onEditQuantities && (
              <button type="button" className="rpv-btn" onClick={onEditQuantities}>
                ✎ Edit quantities
              </button>
            )}
            <button type="button" className="rpv-btn" onClick={handleOpenInTab} disabled={!url}>
              Open in new tab
            </button>
            <button type="button" className="rpv-btn rpv-btn-primary" onClick={handleDownload} disabled={!url}>
              ⬇ Download
            </button>
            <button type="button" className="rpv-btn rpv-btn-close" onClick={onClose} aria-label="Close preview">
              ✕
            </button>
          </div>
        </header>

        <div className="rpv-body">
          {!url && <div className="rpv-placeholder">Preparing preview…</div>}

          {url && inlineSupported && (
            <iframe
              className="rpv-frame"
              src={`${url}#toolbar=1&navpanes=0&view=FitH`}
              title="Recipe Book PDF preview"
            />
          )}

          {url && !inlineSupported && (
            // Never a blank rectangle. If the browser cannot show a PDF inline, say so
            // and give the two things that do work here.
            <div className="rpv-placeholder rpv-fallback">
              <p className="rpv-fallback-title">This browser can’t display PDFs inside a page.</p>
              <p>Open the book in a new tab, or download it to read offline.</p>
              <div className="rpv-fallback-actions">
                <button type="button" className="rpv-btn" onClick={handleOpenInTab}>Open in new tab</button>
                <button type="button" className="rpv-btn rpv-btn-primary" onClick={handleDownload}>⬇ Download</button>
              </div>
            </div>
          )}
        </div>

        <footer className="rpv-foot">
          <span className="rpv-foot-note">
            Nothing is saved to your device until you press Download.
          </span>
          <button type="button" className="rpv-btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}
